import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import RefundPolicy from '../components/RefundPolicy';

const TermsConditions = () => {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: 'smooth' }), 150);
  }, [hash]);

  return (
    <div className="min-h-screen bg-black text-white pt-24 md:pt-40 pb-16">
      <div className="container mx-auto px-4 max-w-4xl">
        <h1 className="text-4xl md:text-5xl font-bold mb-8 bg-gradient-to-r from-amber-400 to-amber-600 bg-clip-text text-transparent">
          Terms & Conditions
        </h1>
        
        <div className="space-y-8 text-gray-300">
          <div>
            <p className="text-sm text-gray-400 mb-6">Last updated: {new Date().toLocaleDateString()}</p>
            
            <p className="mb-4">
              Please read these Terms and Conditions carefully before using Visual Creationz services. These Terms are between you and Simon Balfour trading as Visual Creationz ("Visual Creationz", "we", "us"). By placing an order, you agree to be bound by these terms.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Services</h2>
            <p className="mb-4">
              Visual Creationz provides premium cinematic visual storytelling and video creation services. We offer three tiers - Essential Collection, Signature Collection and Cinematic Collection - with varying features and delivery times as specified on our Pricing page.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Orders & Payment</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>A booking deposit (£20, £30 or £50 depending on package) is required at the time of order</li>
              <li>Deposits are fixed per package and do not change when add-ons are selected</li>
              <li>The remaining balance, including any add-ons, is due upon completion before final delivery</li>
              <li>All payments are processed securely through Stripe</li>
              <li>Prices are in GBP (£) and include VAT where applicable</li>
              <li>Orders are confirmed upon receipt of deposit payment</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Content & Materials</h2>
            <p className="mb-4">You are responsible for:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Providing high-quality photos and videos</li>
              <li>Ensuring you own the rights to all submitted materials</li>
              <li>Obtaining necessary permissions for any third-party content</li>
              <li>Ensuring content does not violate any laws or third-party rights</li>
            </ul>
            <p className="mt-4">
              Visual Creationz reserves the right to refuse content that is inappropriate, illegal, or violates intellectual property rights.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Delivery Times</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Essential Collection:</strong> 1-3 days (estimated)</li>
              <li><strong>Signature Collection:</strong> 2-4 days (estimated)</li>
              <li><strong>Cinematic Collection:</strong> 3-5 days (estimated)</li>
              <li><strong>Priority Delivery Add-on:</strong> 24 hours</li>
            </ul>
            <p className="mt-4">
              These are estimated production times, not guaranteed delivery dates. Delivery times begin once all materials and requirements are received. Delays in providing materials will affect delivery dates. Physical USB delivery times may vary depending on postage. See Cancellations and Refunds below for what happens if your order is delayed.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Optional Add-ons</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Priority Delivery (24 hours): £15</li>
              <li>Additional revisions: £5 each</li>
              <li>Personalised Song: Your Story, Your Song: £20 - an original song inspired by the names, occasion, memories, genre, mood and language you provide when ordering</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Revisions</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Essential Collection includes 1 free revision, Signature 2 free revisions and Cinematic 3 free revisions</li>
              <li>Additional revisions are £5 each</li>
              <li>Revision requests must be made within 7 days of delivery</li>
              <li>Revisions cover reasonable changes to the agreed creative direction, not unlimited complete redesigns</li>
              <li>Major redesigns or additional content requests may incur extra charges</li>
            </ul>
          </div>

          <RefundPolicy id="refunds" />

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Intellectual Property</h2>
            <p className="mb-4">
              Upon full payment, you receive all rights to the final memory film. However:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Visual Creationz retains the right to showcase work in portfolios</li>
              <li>We may use anonymized projects for marketing purposes</li>
              <li>You can request exclusion from portfolio showcasing</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Delivery Method</h2>
            <p className="mb-4">
              Final memory films are delivered via:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Premium USB drive (included in all packages)</li>
              <li>Secure private Google Drive download link (available for 30 days)</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Limitation of Liability</h2>
            <p className="mb-4">
              Visual Creationz is not liable for any indirect, incidental, or consequential damages arising from the use of our services. Our total liability is limited to the amount paid for the service.
            </p>
            <p className="mb-4" data-testid="terms-liability-statutory">
              Nothing in these Terms excludes or limits our liability where it would be unlawful to do so, and nothing affects your statutory rights as a consumer.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Changes to Terms</h2>
            <p className="mb-4">
              We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting. Continued use of our services constitutes acceptance of modified terms.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Contact Information</h2>
            <p className="mb-4">
              For questions about these Terms & Conditions, please contact:
            </p>
            <ul className="space-y-2" data-testid="terms-contact-details">
              <li>Business: Simon Balfour trading as Visual Creationz</li>
              <li>Email: visualcreationz@hotmail.com</li>
              <li>Phone: 07494 164379</li>
              <li>Website: www.visualcreationz.com</li>
            </ul>
          </div>

          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-6 mt-8">
            <p className="text-white font-semibold">
              By placing an order with Visual Creationz, you acknowledge that you have read, understood, and agree to these Terms and Conditions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsConditions;
